import { FlexBox, Icon, IconButton, Table, Text } from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { useContext, useRef } from 'react';

import { CustomFormField } from 'app/views/layout/views/settings/views/customForms/customForms.types';
import { getHumanReadableFormFieldType } from 'app/views/layout/views/settings/views/customForms/views/formsEditPage/utils/humanReadableFormFieldType';
import { DragMoveEvent, useOrderedDragDrop } from 'utils/useOrderedDragDrop';

import { FieldsListModalsContext } from '../../../hooks/FieldsModalsContext';

import styles from './formFieldRow.module.scss';

const type = Symbol('formField');

type Props = {
  field: CustomFormField;
  index: number;
  onOrderChange: (initialRowIndex: number, finalRowIndex: number) => void;
  deleteButton: JSX.Element;
};

export function FormFieldRow({
  field,
  index,
  onOrderChange,
  deleteButton,
}: Props): JSX.Element {
  const { fieldDetailsModal } = useContext(FieldsListModalsContext);
  const dragHandleRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLTableRowElement>(null);

  const onMove = ({
    dragIndex: sourceIndex,
    hoverIndex: destinationIndex,
  }: DragMoveEvent) => {
    onOrderChange(sourceIndex, destinationIndex);
  };

  const { isDragging, isOver } = useOrderedDragDrop({
    type,
    index,
    onMove,
    target: rowRef,
    preview: rowRef,
    triggerSwap: 'drop',
    source: dragHandleRef,
  });

  const rowCellClassNames = classNames({ [styles.rowCellOver]: isOver });

  return (
    <Table.Tr ref={rowRef} style={{ opacity: isDragging ? 0.4 : 1 }}>
      <Table.Td className={rowCellClassNames}>
        <div ref={dragHandleRef} className={styles.dragHandle}>
          <Icon name="drag" />
        </div>
      </Table.Td>

      <Table.Td className={rowCellClassNames}>
        <Text.Paragraph variant="3">{field.title}</Text.Paragraph>
      </Table.Td>

      <Table.Td className={rowCellClassNames}>
        <Text.Paragraph variant="4" wrap="nowrap">
          {getHumanReadableFormFieldType(field.type)}
        </Text.Paragraph>
      </Table.Td>

      <Table.Td className={rowCellClassNames}>
        <FlexBox justifyContent="end" alignItems="center" gap="300">
          <IconButton
            icon="pen"
            variant="tertiary"
            type="button"
            onClick={() => fieldDetailsModal.open(index)}
          />

          {deleteButton}
        </FlexBox>
      </Table.Td>
    </Table.Tr>
  );
}
